/**
 * AVBP Clients module — TypeScript types.
 * Authoritative source: Owner's Inbox/04_Projects/AVBP/AVBP_Clients_Module_Spec.md v1.2 §4.1
 *
 * Any divergence from the spec is a bug — flag and fix, don't improvise.
 */

import type { Timestamp } from 'firebase/firestore';

// ─── Enums ───────────────────────────────────────────────────────────────

export type FirmId = 'firm1' | 'firm2';
export type FirmPrefix = 'AV1' | 'RKH';

/** `firmId → prefix` map — locked per spec §4.1 row 3. */
export const FIRM_PREFIX: Record<FirmId, FirmPrefix> = {
  firm1: 'AV1',
  firm2: 'RKH',
};

export type Currency = 'INR' | 'USD';

export type Intent = 'buying' | 'living' | 'building' | 'not_sure';

export type Geo = 'IN' | 'INTL';

export type ServiceInterest =
  | 'L1A'
  | 'L1B'
  | 'L2A'
  | 'L2B'
  | 'L2C'
  | 'L2D'
  | 'L3A'
  | 'L3B'
  | 'undecided';

export type LeadSource =
  | 'E1'
  | 'E2'
  | 'E3'
  | 'E4'
  | 'home_form'
  | 'exit_popup'
  | 'property_score'
  | 'room_assessment'
  | 'plot_check'
  | 'guide_download'
  | 'service_page'
  | 'contact_page'
  | 'contact'
  | 'service_enquiry'
  | 'newsletter'
  | 'manual_entry'
  | 'referral';

export type ClientStatus = 'active' | 'completed' | 'on_hold' | 'archived';

export type Tier = 'L1' | 'L2' | 'L3';

export type BookingType =
  | 'clarity_call'
  | 'L1A'
  | 'L1B'
  | 'L2A'
  | 'L2B'
  | 'L2C'
  | 'L2D'
  | 'L3A'
  | 'L3B'
  | 'milestone_followup'
  | 'other';

export type PaymentStatus =
  | 'pending'
  | 'confirmed'
  | 'failed'
  | 'refunded'
  | 'partial_refund';

export type PaymentFirm = 'AV' | 'RKH'; // note: payments use short firm ID not firmId enum

// ─── Sub-shapes ──────────────────────────────────────────────────────────

export interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
}

export interface AltContact {
  label: string;
  phone: string;
  relation:
    | 'spouse'
    | 'parent'
    | 'child'
    | 'sibling'
    | 'partner'
    | 'architect'
    | 'builder'
    | 'assistant'
    | 'other';
}

export interface ModuleFlags {
  hasQuotes: boolean;
  hasProjects: boolean;
  hasPrescription: boolean;
  hasAppointments: boolean;
  hasPayments: boolean;
  hasDrawings: boolean;
  openTaskCount: number;
}

// ─── Core Client document ────────────────────────────────────────────────

export interface Client {
  // Identity
  avbpClientId: string; // doc ID — `{prefix}-{FYYY}-{NNNN}`
  partyId: string; // cross-firm human identity key
  firmId: FirmId;
  schemaVersion: number; // = 1 for v1
  sourceLeadId: string | null;

  // Contact
  name: string;
  displayName?: string | null;
  email: string;
  phone: string; // E.164
  whatsapp?: string | null;
  whatsappOptIn: boolean;
  altContact?: AltContact[];

  // Firm & invoicing
  company?: string | null;
  gstin?: string | null;
  billingAddress?: Address;
  shippingAddress?: Address | 'same';
  currency: Currency;

  // Origin / audit
  leadSource: LeadSource;
  sourcePage?: string | null;
  intent?: Intent | null;
  initialServiceInterest?: ServiceInterest | null;
  decisionMaker?: boolean | null;
  geo: Geo;
  toolScoreAtConversion?: number | null;
  referredBy?: string | null;
  leadCreatedAt?: Timestamp | null;

  // Assignment & status
  assignedTo: string;
  status: ClientStatus;
  tier?: Tier;

  // Module activity flags (denormalised)
  modules: ModuleFlags;

  // Integrations
  brevoContactId?: string | null;
  brevoListIds?: string[];
  aisensyContactId?: string | null;
  aisensyTags?: string[];
  portalToken: string;
  portalTokenExpiresAt?: Timestamp | null;
  portalLastAccessedAt?: Timestamp | null;
  keepPortalActive: boolean; // row 42b — default false; pre-AVBP imports = true
  archivedAt?: Timestamp | null; // set when status flips to archived (used by auto-revoke)

  // Meta
  summary?: string | null;
  tags?: string[];
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
  updatedBy: string;
  deletedAt?: Timestamp | null;
}

// ─── Party match query result ────────────────────────────────────────────

export interface PartyMatch {
  matched: boolean;
  partyId: string;
  matchedClientId?: string;
  matchedFirmId?: FirmId;
  reason?: string;
}

// ─── Team & Firm types (Sprint 2) ────────────────────────────────────────

/**
 * Roles per Varun's Sprint 2 dispatch (supersedes the v1.2 spec list).
 * Spec called these admin|sales|consultant|viewer|client. Dispatch updated to
 * admin|sales|delivery|accounts|ops. The Firestore rules still treat the
 * legacy `consultant` role as an alias of `delivery` until rules are next
 * deployed (Sprint 3). For now, both are accepted on the read side.
 */
export type TeamRole = 'admin' | 'sales' | 'delivery' | 'accounts' | 'ops';

export interface TeamUser {
  userId: string;
  name: string;
  email: string;
  role: TeamRole;
  pinHash: string; // SHA-256 of (salt + pin)
  salt: string;
  active: boolean;
  createdAt: Timestamp | Date | null;
  lastLoginAt?: Timestamp | Date | null;
  mustChangePin?: boolean; // seeded users carry this
}

export interface FirmDoc {
  firmId: FirmId;
  prefix: FirmPrefix;
  legalName: string;
  shortName: string;
  gstApplies: boolean; // firm1 = true, firm2 = false
  defaultCurrency: Currency;
  invoicePrefix: FirmPrefix;
  bankDetails?: {
    accountName?: string;
    accountNumber?: string;
    ifsc?: string;
    bankName?: string;
  };
  registeredAddress?: Address;
  active: boolean;
  createdAt: Timestamp | Date | null;
}

// ─── Audit log entry (per dispatch §11) ──────────────────────────────────

export interface AuditLogEntry {
  clientId: string;
  userId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  at: Timestamp | Date | null;
}

// ─── Payment doc (stub — per Payments Stub v0.2 §2.1) ────────────────────

export interface PaymentDoc {
  paymentId: string;
  clientId: string | null;
  leadId: string | null;
  firm: PaymentFirm;
  amount: number;
  currency: Currency;
  paymentMethod:
    | 'razorpay'
    | 'paypal'
    | 'bank_transfer'
    | 'upi'
    | 'cash'
    | 'cheque'
    | 'other';
  status: PaymentStatus;
  bookingType: BookingType;
  milestoneIndex?: number;
  invoiceNumber?: string | null;
  gstApplied: boolean;
  gstAmount?: number | null;
  createdAt: Timestamp;
  paidAt?: Timestamp | null;
  createdBy: string;
  updatedAt: Timestamp;
  notes?: string | null;
}
